import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../../helpers/withRouter'

import { Button, Page, Spinner, Title, ValidatedInput } from '../../../components'
import CountryPickerModal from '../../../containers/CountryPickerModal'
import countries from '../../../countries.json'
import { ICountry } from '../../../models/country'
import { isEmailValid, isTelephoneValid, isVerificationCodeValid } from '../../../helpers'
import { urls } from '../../../routing'
import { LiteralsService } from '../../../services'
import {
  confirmVerificationCodeAction,
  setVerificationCallingCodeAction,
  setVerificationCodeAction,
  setVerificationCountryAction,
  setVerificationEmailAction,
  setVerificationPhoneAction
} from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import StyledArticle from '../styles'
import { appsLogosSelected, appsStyleSelected, appsUsernameSelected } from '../../../helpers/app'
import { AnyAction } from 'redux'

const isPhoneVerification = appsUsernameSelected?.username === 'phone'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  dni: string
  email: string
  phone: string
  code: string
  isVerificating: boolean
  country?: ICountry
  callingCode: string
}

class AccountVerificationStep extends PureComponent<IProps> {
  constructor(props: IProps) {
    super(props)
    if (isPhoneVerification && !props.country) {
      const defaultCountry = countries.find(country => country.code === 'ES')
      if (defaultCountry) {
        props.dispatch(setVerificationCountryAction(defaultCountry))
        props.dispatch(setVerificationCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get buttonIsDisabled(): boolean {
    const { email, phone, code, isVerificating } = this.props

    if (isPhoneVerification) return !isTelephoneValid(phone) || !isVerificationCodeValid(code) || isVerificating
    return !isEmailValid(email) || !isVerificationCodeValid(code) || isVerificating
  }

  private get verificationButton(): ReactNode {
    const { isVerificating } = this.props
    if (isVerificating) return <Spinner />
    return (
      <Button
        title={ LiteralsService.get('continue') }
        onPress={ this.handleValidatedButtonPress }
        filled
        upper
        isDisabled={ this.buttonIsDisabled }
      />
    )
  }

  public render(): ReactNode {

    const { email, phone, code, country } = this.props

    return (
      <Page className={ appsStyleSelected?.style }>
        <StyledArticle>
          <form onSubmit={ e => e.preventDefault() }>
            <header>
              <img  className={ appsLogosSelected?.classRounded }
                    src={ appsLogosSelected?.logoRounded }
                    alt={ appsLogosSelected?.alt } />
            </header>
            <Title text={ LiteralsService.get('verifyYourEmail', true) } />
            <section>
              {
                isPhoneVerification ? (
                  <ValidatedInput
                    name="phone"
                    phone
                    label={ LiteralsService.get('phone', true) }
                    value={ phone }
                    country={ country }
                    onChange={ this.onPhoneChange }
                    error={ !isTelephoneValid(phone) }
                  />
                ) : (
                  <ValidatedInput
                    name="email"
                    label={ LiteralsService.get('email', true) }
                    value={ email }
                    onChange={ this.onEmailChange }
                    error={ !isEmailValid(email) }
                  />
                )
              }
              <ValidatedInput
                name="code"
                label={ LiteralsService.get('verificationCode', true) }
                value={ code }
                onChange={ this.onCodeChange }
                error={ !isVerificationCodeValid(code) }
              />
            </section>
            { isPhoneVerification && <CountryPickerModal selectCountry={ this.onCountryChange } /> }
            <section className="actions">
              { this.verificationButton }
              <div className="link">
                <span onClick={ this.goToNewConfirmationCode }>
                  { LiteralsService.get('requestNewSignUpVerificationCode', true) }
                </span>
              </div>
            </section>
          </form>
        </StyledArticle>
      </Page>
    )
  }

  private onEmailChange = (dni: string) => {
    const { dispatch } = this.props
    dispatch(setVerificationEmailAction(dni))
  }

  private onPhoneChange = (phone: string) => {
    const { dispatch } = this.props
    dispatch(setVerificationPhoneAction(phone))
  }

  private onCountryChange = (country: ICountry) => {
    const { dispatch } = this.props
    dispatch(setVerificationCountryAction(country))
    dispatch(setVerificationCallingCodeAction(country.dial_code))
  }

  private onCodeChange = (code: string) => {
    const { dispatch } = this.props
    dispatch(setVerificationCodeAction(code))
  }

  private handleValidatedButtonPress = () => {
    const { dispatch } = this.props
    dispatch(confirmVerificationCodeAction())
  }

  private goToNewConfirmationCode = () => {
    this.props.history.push(urls.accountVerificationResend)
  }
}

const mapStateToProps = ({ signUp, verification }: IStoreState) => ({
  dni: verification.dni ? verification.dni : signUp.dni,
  email: verification.email ? verification.email : signUp.email,
  phone: verification.phone ? verification.phone : signUp.phone,
  code: verification.code,
  isVerificating: verification.isVerificating,
  country: verification.country,
  callingCode: verification.callingCode
})

export default withRouter(connectRedux(mapStateToProps, AccountVerificationStep))
